
import { Vue, Component } from 'vue-property-decorator';
import {
  getWallet, getTransactions, requestCashRefund, Transaction,
} from '@/api/cash';

const REFUND_ELIGIBLE_DAYS = 7;

@Component({
  name: 'CashRefund',
})
export default class CashRefund extends Vue {
  private currentBalance = 0;

  private eligibleTransactions: Transaction[] = [];

  private selectedUids: string[] = [];

  private isLoading = false;

  private isSubmitting = false;

  get selectedTotal(): number {
    return this.eligibleTransactions
      .filter((t) => this.selectedUids.includes(t.uid))
      .reduce((sum, t) => sum + t.amount, 0);
  }

  get exceedsBalance(): boolean {
    return this.selectedTotal > this.currentBalance;
  }

  get canSubmit(): boolean {
    return this.selectedUids.length > 0 && !this.exceedsBalance;
  }

  async created(): Promise<void> {
    await Promise.all([
      this.loadWallet(),
      this.loadEligibleTransactions(),
    ]);
  }

  private async loadWallet(): Promise<void> {
    try {
      const response = await getWallet();
      if (response.data) {
        this.currentBalance = response.data.balance || 0;
      }
    } catch (error) {
      console.warn('지갑 정보 로드 실패:', error);
    }
  }

  private async loadEligibleTransactions(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await getTransactions({ page: 0, size: 100 });
      const all: Transaction[] = Array.isArray(response.data)
        ? response.data
        : (response.data && response.data.content) || [];

      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - REFUND_ELIGIBLE_DAYS);

      this.eligibleTransactions = all.filter((t) => (
        t.transactionType === 'CHARGE' && new Date(t.createdDate) >= cutoff
      ));
    } catch (error) {
      console.error('충전 내역 로드 실패:', error);
      this.$message.error('충전 내역을 불러오는데 실패했습니다.');
    } finally {
      this.isLoading = false;
    }
  }

  private isSelected(uid: string): boolean {
    return this.selectedUids.includes(uid);
  }

  private toggleSelect(uid: string): void {
    if (this.isSelected(uid)) {
      this.selectedUids = this.selectedUids.filter((u) => u !== uid);
    } else {
      this.selectedUids = [...this.selectedUids, uid];
    }
  }

  private formatCurrency(value: number): string {
    return value.toLocaleString('ko-KR');
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  }

  private async submitRefund(): Promise<void> {
    if (!this.canSubmit) return;

    this.isSubmitting = true;
    try {
      await requestCashRefund({
        transactionUids: this.selectedUids,
        amount: this.selectedTotal,
      });
      this.$message.success('환불 신청이 접수되었습니다.');
      this.$router.push({ name: 'CashHistory' });
    } catch (error) {
      console.error('환불 신청 실패:', error);
      this.$message.error('환불 신청에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      this.isSubmitting = false;
    }
  }
}
